import React, { useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    TextField,
    debounce,
    useTheme,
    IconButton,
    Tooltip
} from '@mui/material';
import { googleMapsService } from '../../services/googleMapsService';
import MapIcon from '@mui/icons-material/Map';

interface GoogleMapsAutocompleteProps {
    value: string;
    onChange: (address: string, latitude?: number, longitude?: number) => void;
    defaultBounds?: {
        city?: string;
        country?: string;
    };
    label?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    readOnly?: boolean;
}

interface Prediction {
    description: string;
    place_id: string;
}

const GoogleMapsAutocomplete: React.FC<GoogleMapsAutocompleteProps> = ({
    value,
    onChange,
    defaultBounds,
    label = 'Address',
    required = false,
    error = false,
    helperText,
    readOnly = false
}) => {
    const theme = useTheme();
    const [inputValue, setInputValue] = useState('');
    const [predictions, setPredictions] = useState<Prediction[]>([]);
    const [loading, setLoading] = useState(false);
    const [apiLoaded, setApiLoaded] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);

    // Initialize Google Maps API once
    useEffect(() => {
        const initializeAPI = async () => {
            try {
                await googleMapsService.initializeGoogleMaps();
                setApiLoaded(true);
            } catch (error) {
                console.error('Error initializing Google Maps API:', error);
            }
        };
        
        initializeAPI();
    }, []);

    // Initialize map when value exists and API is loaded
    useEffect(() => {
        if (!value || !apiLoaded || !mapContainerRef.current || mapRef.current) return;
        
        try {
            console.log('Initializing map...');
            const map = new google.maps.Map(mapContainerRef.current, {
                zoom: 17,
                center: { lat: 0, lng: 0 },
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                zoomControl: true,
                zoomControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_CENTER
                },
                mapId: 'MINDSHARE_MAP'
            });
            mapRef.current = map;
            console.log('Map initialized successfully');
        } catch (error) {
            console.error('Error initializing map:', error);
        }
    }, [value, apiLoaded]);

    // Update map location when value changes and map is ready
    useEffect(() => {
        const updateMapLocation = async () => {
            if (!value || !apiLoaded || !mapRef.current) return;

            try {
                console.log('Geocoding address:', value);
                const result = await googleMapsService.getGeocode(value);
                if (result && result.geometry) {
                    const { location } = result.geometry;
                    const lat = location.lat();
                    const lng = location.lng();
                    console.log('Location found:', { lat, lng });
                    
                    setCoordinates({ lat, lng });
                    
                    // Trigger resize to ensure map displays correctly
                    google.maps.event.trigger(mapRef.current, 'resize');
                    
                    mapRef.current.setCenter(location);
                    mapRef.current.setZoom(17);

                    if (markerRef.current) {
                        markerRef.current.position = location;
                        console.log('Updated existing marker');
                    } else {
                        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
                            position: location,
                            map: mapRef.current
                        });
                        console.log('Created new marker');
                    }

                    onChange(value, lat, lng);
                }
            } catch (error) {
                console.error('Error updating map location:', error);
            }
        };

        updateMapLocation();
    }, [value, apiLoaded]);

    const handleAddressSelect = async (address: string) => {
        if (!address || !apiLoaded) return;

        try {
            console.log('Selected address:', address);
            const result = await googleMapsService.getGeocode(address);
            if (result && result.geometry) {
                const { location } = result.geometry;
                const lat = location.lat();
                const lng = location.lng();
                
                setCoordinates({ lat, lng });
                
                if (mapRef.current) {
                    google.maps.event.trigger(mapRef.current, 'resize');
                    mapRef.current.setCenter(location);
                    mapRef.current.setZoom(17);

                    if (markerRef.current) {
                        markerRef.current.position = location;
                    } else {
                        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
                            position: location,
                            map: mapRef.current
                        });
                    }
                }

                onChange(address, lat, lng);
            }
        } catch (error) {
            console.error('Error geocoding selected address:', error);
        }
    };

    const openInGoogleMaps = () => {
        if (value) {
            const encodedAddress = encodeURIComponent(value);
            const url = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
            window.open(url, '_blank');
        } else if (coordinates) {
            const url = `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
            window.open(url, '_blank');
        }
    };

    const fetchPredictions = debounce(async (input: string) => {
        if (!input) {
            setPredictions([]);
            return;
        }

        setLoading(true);
        try {
            let bounds;
            const country = defaultBounds?.country;

            console.log('GoogleMapsAutocomplete: Fetching predictions with defaultBounds:', {
                city: defaultBounds?.city,
                country: defaultBounds?.country,
                input
            });

            // Geocode the default city to get bounds for biasing (ranking preference)
            // Country will be used as a hard restriction (only show that country)
            // City bounds will bias/rank results from that city higher, but still show other cities
            if (defaultBounds?.city) {
                const boundsAddress = [defaultBounds.city, defaultBounds.country]
                    .filter(Boolean)
                    .join(', ');
                    
                console.log('GoogleMapsAutocomplete: Geocoding city for location biasing:', boundsAddress);
                
                if (boundsAddress) {
                    const result = await googleMapsService.getGeocode(boundsAddress);
                    if (result && result.geometry && result.geometry.viewport) {
                        bounds = result.geometry.viewport;
                        console.log('GoogleMapsAutocomplete: City bounds will BIAS results (prefer, not restrict):', {
                            sw: bounds.getSouthWest().toJSON(),
                            ne: bounds.getNorthEast().toJSON()
                        });
                    } else {
                        console.warn('GoogleMapsAutocomplete: Could not geocode city for biasing:', boundsAddress);
                    }
                }
            }

            const results = await googleMapsService.getPlacePredictions(input, bounds, country);
            console.log(`GoogleMapsAutocomplete: Received ${results.length} predictions`);
            
            setPredictions(results.map(prediction => ({
                description: prediction.description,
                place_id: prediction.place_id
            })));
        } catch (error) {
            console.error('Error fetching predictions:', error);
            setPredictions([]);
        } finally {
            setLoading(false);
        }
    }, 300);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                    <Autocomplete
                        freeSolo
                        value={value}
                        inputValue={inputValue}
                        onInputChange={(_, newInputValue) => {
                            if (!readOnly) {
                                setInputValue(newInputValue);
                                fetchPredictions(newInputValue);
                            }
                        }}
                        onChange={(_, newValue) => {
                            if (!readOnly) {
                                if (typeof newValue === 'string') {
                                    handleAddressSelect(newValue);
                                } else if (newValue && typeof newValue === 'object') {
                                    handleAddressSelect(newValue.description);
                                }
                            }
                        }}
                        options={predictions}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                                return option;
                            }
                            return option.description;
                        }}
                        loading={loading}
                        readOnly={readOnly}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                required={required}
                                error={error}
                                helperText={helperText}
                                autoComplete="off"
                                InputProps={{
                                    ...params.InputProps,
                                    readOnly: readOnly,
                                    endAdornment: (
                                        <>
                                            {loading && <CircularProgress color="inherit" size={20} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                </Box>
                {value && (
                    <Tooltip title="Open in Google Maps">
                        <IconButton 
                            onClick={openInGoogleMaps}
                            color="primary"
                            sx={{ mt: 1 }}
                        >
                            <MapIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            
            {value && (
                <Box
                    ref={mapContainerRef}
                    className="google-map-container"
                    sx={{
                        height: 200,
                        width: '100%',
                        marginTop: 2,
                        marginBottom: 2,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.divider}`,
                        position: 'relative',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        '& > div': {
                            width: '100% !important',
                            height: '100% !important'
                        }
                    }}
                />
            )}
        </Box>
    );
};

export default GoogleMapsAutocomplete; 